import { MarketplaceResultType } from "config";
import { Hit } from "_shared/_api/search/search-results";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import CompanyResult from "marketplace/_views/company-result";
import IndividualResult from "marketplace/_views/individual-result";
import TeamResult from "marketplace/_views/team-result";
import MarketplaceCompanyInfoSchema from "marketplace/_schema/marketplace-company-info-schema";
import Individual from "marketplace/individuals/individual";
import MarketplaceIndividualInfoSchema from "marketplace/_schema/marketplace-individual-info-schema";
import React from "react";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import Team from "marketplace/teams/team";
import MarketplaceTeamInfoAPIResponse from "marketplace/team-profile/_api/response-contracts/marketplace-team-info-api-response";

type ResultsListProps = {
  results: Hit<MarketplaceSearchInfoJSON>[];
  embedded?: boolean;
  selectingTeamLeader?: boolean;
  selectingTeamMember?: boolean;
  selectingTeam?: boolean;
  selectedTeamMemberUserIds?: Guid[];
  navigate?: NavigateFunction;
  onTeamMemberSelected?: (teamMember: Individual) => void;
  onTeamSelected?: (team: Team) => void;
};

export default function ResultsList(props: Readonly<ResultsListProps>) {
  const {
    results,
    embedded,
    selectingTeamLeader,
    selectingTeamMember,
    selectingTeam,
    selectedTeamMemberUserIds,
    navigate,
    onTeamMemberSelected,
    onTeamSelected,
  } = props;

  const { openDialog } = useDialog();
  const session = useSession();

  function handleResultClicked(
    _event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth = false
  ) {
    if (!embedded) {
      navigate?.(`/marketplace/${profileType.toLowerCase()}/${profileId.value}`);
      return;
    }
    openDialog({
      component: component,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: fullWidth,
      },
    });
  }

  return (
    <React.Fragment>
      {results?.map((hit) => {
        if (
          hit.data?.type === MarketplaceResultType.Company &&
          !selectingTeam &&
          !selectingTeamLeader &&
          !selectingTeamMember
        ) {
          const companyResult = MarketplaceCompanyInfoSchema.validateJSON(hit.data);
          return (
            <CompanyResult
              key={companyResult.id}
              hit={hit}
              result={companyResult}
              handleResultClicked={handleResultClicked}
              getText={getText}
            />
          );
        }

        if (hit.data?.type === MarketplaceResultType.Team && !selectingTeamLeader && !selectingTeamMember) {
          const teamResult = Object.assign(new MarketplaceTeamInfoAPIResponse(), hit.data); // TODO: Is this necessary?
          return (
            <TeamResult
              key={teamResult.id}
              embedded={embedded ?? false}
              hit={hit}
              result={teamResult}
              handleResultClicked={handleResultClicked}
              getText={getText}
              session={session}
              navigate={navigate}
              onTeamSelected={onTeamSelected}
            />
          );
        }

        if (hit.data?.type === MarketplaceResultType.Individual && !selectingTeam) {
          const individualResult = MarketplaceIndividualInfoSchema.validateJSON(hit.data);
          return (
            <IndividualResult
              selectingAsLeader={selectingTeamLeader}
              selectedTeamMemberUserIds={selectedTeamMemberUserIds}
              key={individualResult.id}
              embedded={embedded ?? false}
              hit={hit}
              result={individualResult}
              session={session}
              navigate={navigate}
              handleResultClicked={handleResultClicked}
              getText={getText}
              onTeamMemberSelected={onTeamMemberSelected}
            />
          );
        }

        return null;
      })}
    </React.Fragment>
  );
}

function getText(
  result: Hit<MarketplaceSearchInfoJSON>,
  fieldNames: string[],
  base: unknown = result?.data,
  noCommas: boolean = false
) {
  let html: string = "";

  for (let index = 0; index < fieldNames.length; index++) {
    const value = fieldNames[index];
    const isLastLoop: boolean = index === fieldNames.length - 1;
    let comma: string = noCommas || isLastLoop ? "" : ",";

    // If value is invalid, and we're on the last iteration then remove the last comma
    if (!base?.[value as keyof unknown] && isLastLoop) html = html.slice(0, -1);

    // Value is invalid, skip
    if (!base?.[value as keyof unknown]) continue;

    if (result?.highlight?.[value]) html += ` ${result?.highlight?.[value][0]}${comma}`;
    else html += ` ${base?.[value as keyof unknown]}${comma}`;
  }

  return { __html: html };
}
